import _axios, {
  type AxiosError,
  type InternalAxiosRequestConfig,
} from "axios";
import { isRetryableNetworkError, waitForOnline } from "./network-error";

export const axios = _axios.create({
  baseURL: "/api",
  withCredentials: true,
});

const MAX_NETWORK_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 500;
const ONLINE_WAIT_MS = 2500;

const RETRYABLE_READ_METHODS = new Set(["get", "head", "options"]);

type RetryableConfig = InternalAxiosRequestConfig & {
  __networkRetryCount?: number;
};

type LogoutLocation = Pick<Location, "pathname" | "replace">;

export function createLogoutRedirectHandler() {
  let redirectStarted = false;

  return (location: LogoutLocation) => {
    if (redirectStarted || location.pathname === "/logout") {
      return;
    }

    redirectStarted = true;
    location.replace("/logout");
  };
}

const redirectToLogout = createLogoutRedirectHandler();

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401 && typeof window !== "undefined") {
      redirectToLogout(window.location);
      return Promise.reject(error);
    }

    const config = error.config as RetryableConfig | undefined;
    const method = config?.method?.toLowerCase();

    if (
      config &&
      method &&
      RETRYABLE_READ_METHODS.has(method) &&
      isRetryableNetworkError(error)
    ) {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return Promise.reject(error);
      }

      const attempt = config.__networkRetryCount ?? 0;
      if (attempt < MAX_NETWORK_RETRIES) {
        config.__networkRetryCount = attempt + 1;
        await waitForOnline(ONLINE_WAIT_MS);
        await delay(RETRY_BASE_DELAY_MS * 2 ** attempt);
        return axios(config);
      }
    }

    return Promise.reject(error);
  }
);
